import type {
  MarketplaceEnablementValue,
  PlanFeatureEnablementValue,
  RawLdEnablementFlagValue,
  RawMarketplaceCatalogLdValue,
} from './enablement.types';

/**
 * Resolver from `useFeatureFlags().getFlag`. Returns {@link RawLdEnablementFlagValue}; enablement keys
 * should still resolve to catalog JSON in production.
 */
export type GetFlagFn = (name: string) => RawLdEnablementFlagValue;

/**
 * Merges all plan enablement flag values into a single map (product code → feature codes).
 * Flags that are unset or invalid are skipped; no plans are assumed enabled by default.
 * When two flags provide data for the same product, definitions are merged (union of features)
 * and an error is logged (no throw).
 *
 * @param getFlag - Resolver for flag values (e.g. useFeatureFlags().getFlag)
 * @param planEnablementFlagNames - List of LaunchDarkly flag keys for plan enablement
 * @returns Normalized {@link PlanFeatureEnablementValue}; empty object if no flags are set or valid
 */
export function getMergedPlanEnablement(
  getFlag: GetFlagFn,
  planEnablementFlagNames: string[]
): PlanFeatureEnablementValue {
  const merged: PlanFeatureEnablementValue = {};
  for (const flagName of planEnablementFlagNames) {
    const value = getFlag(flagName);
    if (value == null || typeof value !== 'object' || Array.isArray(value)) {
      continue;
    }
    const obj = value as Record<string, unknown>;
    for (const productCode of Object.keys(obj)) {
      const features = obj[productCode];
      if (!Array.isArray(features)) continue;
      const normalized = (features as unknown[])
        .filter((f) => f != null)
        .map((f) => String(f));
      const existing = merged[productCode];
      if (existing !== undefined) {
        console.error(
          `[plan enablement] Two enablement flags provide definitions for the same plan/product "${productCode}". Consolidate to a single source or accept merged feature list.`
        );
      }
      merged[productCode] = [...new Set([...(existing ?? []), ...normalized])];
    }
  }
  return merged;
}

/**
 * True when merged plan enablement **P** contains at least one product key (any plan LD flags produced
 * valid catalog rows). When false, **P** is empty: plan enablement denies everywhere until LD returns
 * non-empty catalogs (same contract as {@link isFeatureEnabledForProducts},
 * {@link hasMarketplacePlanCatalogOverlap}, and {@link resolveMarketplaceAndPlanManagementAccess}).
 *
 * @param mergedPlanEnablement - Result of {@link getMergedPlanEnablement}
 */
export function hasMergedPlanEnablementData(
  mergedPlanEnablement: PlanFeatureEnablementValue
): boolean {
  return Object.keys(mergedPlanEnablement).length > 0;
}

/**
 * Returns whether the given feature code is enabled in the merged plan enablement map.
 * Assumes feature codes are unique across plans (no two plans use the same feature code).
 *
 * This answers “does **any** product row list this feature?” It is **not** the same contract as
 * {@link isFeatureEnabledForProducts} or {@link isFeatureEnabledForOrgProductEntitlements} in
 * `entitlements.utils`, which require the feature under an org- or asset-scoped product (pairwise
 * gating for `useEntitlements().hasFeature` / `hasFeatureAccess`).
 *
 * @param mergedPlanEnablement - Result of getMergedPlanEnablement()
 * @param featureCode - Feature code to check (e.g. 'MAPS')
 * @returns true if any product in the map lists this feature code
 * @see isFeatureEnabledForProducts — asset product codes from enablements
 * @see isFeatureEnabledForOrgProductEntitlements — org license rows paired with plan map
 */
export function isFeatureEnabledInPlanEnablement(
  mergedPlanEnablement: PlanFeatureEnablementValue,
  featureCode: string
): boolean {
  return Object.values(mergedPlanEnablement).some((features) =>
    features.includes(featureCode)
  );
}

/**
 * Returns whether the given feature is enabled for the asset via one of its products.
 * Used to gate feature access so that the feature must be enabled by the same plan/product
 * that the enablements data associates with the asset (avoids cross-plan false positives).
 *
 * **Empty merged map (`{}`):** Returns **`false`** — aligned with {@link isMergedPlanProductFeatureEnabled}
 * and marketplace / plan-management rules: no plan rows means plan does not allow access. Rollout uses permissive LD
 * default catalogs, not empty **P** in app logic.
 *
 * When **P** is non-empty but `productCodes` is empty, returns **false** (no product to pair with the
 * feature — avoids treating the feature as enabled because it appears under any product in LD).
 *
 * @param mergedPlanEnablement - Result of getMergedPlanEnablement()
 * @param featureCode - Feature code to check (e.g. 'MAPS')
 * @param productCodes - Product codes for the asset (from enablements.products)
 * @returns true when **P** is non-empty and at least one of `productCodes` lists the feature
 */
export function isFeatureEnabledForProducts(
  mergedPlanEnablement: PlanFeatureEnablementValue,
  featureCode: string,
  productCodes: string[]
): boolean {
  if (!hasMergedPlanEnablementData(mergedPlanEnablement)) {
    return false;
  }
  if (productCodes.length === 0) {
    return false;
  }
  return productCodes.some(
    (code) => mergedPlanEnablement[code]?.includes(featureCode) === true
  );
}

/**
 * Returns whether the given product code is enabled in the merged plan enablement map.
 * A plan/product is enabled only if it appears in the map and has at least one feature
 * (empty feature list is treated as disabled, since all plan logic is feature-code driven).
 *
 * @param mergedPlanEnablement - Result of getMergedPlanEnablement()
 * @param productCode - Product code to check (e.g. 'OST')
 * @returns true if the product is in the map and has at least one enabled feature
 */
export function isProductEnabledInPlanEnablement(
  mergedPlanEnablement: PlanFeatureEnablementValue,
  productCode: string
): boolean {
  const features = mergedPlanEnablement[productCode];
  return Array.isArray(features) && features.length > 0;
}

/**
 * True when merged plan LD data exists, the product row is non-empty, and the feature is listed
 * for that product. This is the plan half of {@link resolveMarketplaceAndPlanManagementAccess} (ANDed
 * with the marketplace catalog); {@link usePlanMarketplaceAccess} exposes the same boolean as `isPlanFeatureEnabled`.
 *
 * **Empty merged map:** Always **`false`** — same as {@link isFeatureEnabledForProducts} for empty **P**.
 *
 * This is **not** the same question as {@link isFeatureEnabledInPlanEnablement} (“feature appears
 * under any product in the merged map”).
 *
 * @param mergedPlanEnablement - Normalized merged map ({@link PlanFeatureEnablementValue})
 * @param productCode - Product code (e.g. 'OST')
 * @param featureCode - Feature code (e.g. 'MAPS')
 */
export function isMergedPlanProductFeatureEnabled(
  mergedPlanEnablement: PlanFeatureEnablementValue,
  productCode: string,
  featureCode: string
): boolean {
  if (!hasMergedPlanEnablementData(mergedPlanEnablement)) {
    return false;
  }
  return (
    isProductEnabledInPlanEnablement(mergedPlanEnablement, productCode) &&
    mergedPlanEnablement[productCode]?.includes(featureCode) === true
  );
}

/**
 * Creates a checker that returns whether a product code appears in **P** with a non-empty feature list.
 * When the merged map is empty, the checker always returns **`false`** (aligned with {@link useEntitlements} `hasProduct`).
 *
 * @param getFlag - Resolver for flag values (e.g. useFeatureFlags().getFlag)
 * @param planEnablementFlagNames - List of LaunchDarkly flag keys for plan enablement
 * @returns Function (productCode: string) => boolean
 */
export function createPlanProductEnablementChecker(
  getFlag: GetFlagFn,
  planEnablementFlagNames: string[]
): (productCode: string) => boolean {
  const merged = getMergedPlanEnablement(getFlag, planEnablementFlagNames);
  return (productCode: string) =>
    isProductEnabledInPlanEnablement(merged, productCode);
}

/**
 * Creates a checker that returns whether a feature code appears under **any** product in the
 * merged plan map. When the merged map is empty, always returns **`false`**.
 *
 * **Warning:** This is **not** the same contract as {@link useEntitlements} `hasFeature`, which
 * gates by `(product, feature)` using org or asset-scoped products. Do not substitute this checker
 * for `hasFeature` without reviewing OSOF / cross-product feature semantics.
 *
 * Not re-exported from `@gm-commercial/entitlements-client` package entry; kept for unit tests and
 * advanced imports from this module path.
 *
 * @param getFlag - Resolver for flag values (e.g. useFeatureFlags().getFlag)
 * @param planEnablementFlagNames - List of LaunchDarkly flag keys for plan enablement
 * @returns Function (featureCode: string) => boolean
 */
export function createPlanEnablementChecker(
  getFlag: GetFlagFn,
  planEnablementFlagNames: string[]
): (featureCode: string) => boolean {
  const merged = getMergedPlanEnablement(getFlag, planEnablementFlagNames);
  return (featureCode: string) =>
    isFeatureEnabledInPlanEnablement(merged, featureCode);
}

/**
 * Returns whether the given product/feature pair is included in a **single raw** LD flag value
 * shaped as `Record<productCode, featureCode[]>` (the same object shape used for one plan flag or
 * for the marketplace catalog object branch).
 *
 * {@link isFeatureInMarketplaceEnablement} delegates its non-array branch here for **parsing reuse**;
 * that does not imply marketplace and plan flags share business meaning—only the JSON shape.
 *
 * @param value - {@link RawLdEnablementFlagValue} from `getFlag` for one **plan** enablement key;
 *   expected production shape is {@link PlanFeatureEnablementValue}; other primitives are invalid and yield false.
 * @param productCode - Product code (e.g. 'OST')
 * @param featureCode - Feature code (e.g. 'MAPS')
 * @returns true if value is a valid catalog object and includes the product/feature pair
 */
export function isFeatureInPlanEnablement(
  value: RawLdEnablementFlagValue,
  productCode: string,
  featureCode: string
): boolean {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const features = (value as Record<string, unknown>)[productCode];
  if (!Array.isArray(features)) {
    return false;
  }
  const normalized = (features as unknown[])
    .filter((f) => f != null)
    .map((f) => String(f));
  return normalized.includes(featureCode);
}

/**
 * Returns whether the given product/feature pair is included in a marketplace enablement flag value.
 * Catalog shape matches plan enablement: `Record<productCode, featureCode[]>`.
 * Legacy shape (during LD migration): `productCode[]` — if the array contains `productCode`, any feature
 * for that product is treated as marketplace-enabled (previous product-only semantics).
 * Non-catalog primitives are invalid and yield false (same defensive rule as plan flags).
 *
 * @param value - {@link RawMarketplaceCatalogLdValue} from `getFlag` for the marketplace key
 * @param productCode - Product code (e.g. 'OST')
 * @param featureCode - Feature code (e.g. 'MAPS')
 * @returns true when the pair is allowed by the catalog, or legacy array lists the product
 */
export function isFeatureInMarketplaceEnablement(
  value: RawMarketplaceCatalogLdValue,
  productCode: string,
  featureCode: string
): boolean {
  if (value == null) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.includes(productCode);
  }
  if (typeof value === 'object' && Object.keys(value).length === 0) {
    return false;
  }
  // Arrays handled above; remaining value is catalog-shaped JSON (same runtime check as plan flags).
  return isFeatureInPlanEnablement(
    value as RawLdEnablementFlagValue,
    productCode,
    featureCode
  );
}

/**
 * Returns whether product key(s) exist in marketplace enablement.
 *
 * This is a key-presence check only: a product counts as present when the object has that
 * product key, even if its feature list is empty. This is used by catalog eligibility logic
 * that needs to know whether marketplace exposes a product row at all, not whether that row
 * contains enabled features.
 *
 * @param marketplaceEnablement - Marketplace-scoped enablement map, typically from
 *   {@link getMergedPlanMarketplaceEnablement}
 * @param productCodes - Product codes that must all exist as keys in `marketplaceEnablement`
 * @returns true when every requested product code exists as an own property on the map
 */
export function hasProductCodeInMarketplaceEnablement(
  marketplaceEnablement: MarketplaceEnablementValue,
  productCodes: string[]
): boolean {
  return productCodes.every((productCode) =>
    Object.hasOwn(marketplaceEnablement, productCode)
  );
}

/**
 * Builds marketplace-scoped plan enablement by intersecting merged plan flags with the marketplace
 * catalog flag.
 *
 * Product keys are included only when they exist in both sources. For included products, feature
 * lists are filtered to the `(product, feature)` pairs allowed by marketplace.
 *
 * Legacy marketplace array values are supported via
 * {@link isFeatureInMarketplaceEnablement} semantics (product-only allow in migration mode).
 *
 * @param getFlag - Resolver for flag values (e.g. useFeatureFlags().getFlag)
 * @param planEnablementFlagNames - Plan enablement flag keys merged via {@link getMergedPlanEnablement}
 * @param marketplaceFlagName - Marketplace catalog flag key resolved via `getFlag`
 * @returns Marketplace-scoped enablement map ({@link MarketplaceEnablementValue})
 */
export function getMergedPlanMarketplaceEnablement(
  getFlag: GetFlagFn,
  planEnablementFlagNames: string[],
  marketplaceFlagName: string
): MarketplaceEnablementValue {
  const mergedPlanEnablements = getMergedPlanEnablement(
    getFlag,
    planEnablementFlagNames
  );

  if (!hasMergedPlanEnablementData(mergedPlanEnablements)) {
    return {};
  }

  const marketplaceFlagValue = getFlag(marketplaceFlagName);
  const merged: MarketplaceEnablementValue = {};
  for (const productCode of Object.keys(mergedPlanEnablements)) {
    const features = mergedPlanEnablements[productCode];
    if (!Array.isArray(features)) {
      continue;
    }

    // Check if product exists in marketplace
    const productInMarketplace = Array.isArray(marketplaceFlagValue)
      ? marketplaceFlagValue.includes(productCode)
      : marketplaceFlagValue != null &&
        typeof marketplaceFlagValue === 'object' &&
        Object.hasOwn(marketplaceFlagValue, productCode);

    // Skip products not in marketplace
    if (!productInMarketplace) {
      continue;
    }

    const allowedFeatures = features
      .filter((featureCode) =>
        isFeatureInMarketplaceEnablement(
          marketplaceFlagValue,
          productCode,
          String(featureCode)
        )
      )
      .map((featureCode) => String(featureCode));

    merged[productCode] = [...new Set(allowedFeatures)];
  }

  return merged;
}

/**
 * Returns whether merged plan enablement (P) and the marketplace catalog flag (M)
 * share at least one `(product, feature)` pair. Uses {@link isFeatureInMarketplaceEnablement}
 * for M so legacy product-only array shapes count during LD migration.
 *
 * Fleet Catalog nav: empty P, or M empty/missing/invalid, yields false (no overlap).
 * Iterates with early return on the first match.
 *
 * @param mergedPlanEnablement - Merged map from {@link getMergedPlanEnablement}
 * @param marketplaceFlagValue - Raw `getFlag` value for the marketplace catalog flag
 */
export function hasMarketplacePlanCatalogOverlap(
  mergedPlanEnablement: PlanFeatureEnablementValue,
  marketplaceFlagValue: RawMarketplaceCatalogLdValue
): boolean {
  if (!hasMergedPlanEnablementData(mergedPlanEnablement)) {
    return false;
  }
  for (const productCode of Object.keys(mergedPlanEnablement)) {
    const features = mergedPlanEnablement[productCode];
    if (!Array.isArray(features)) {
      continue;
    }
    for (const featureCode of features) {
      if (
        isFeatureInMarketplaceEnablement(
          marketplaceFlagValue,
          productCode,
          String(featureCode)
        )
      ) {
        return true;
      }
    }
  }
  return false;
}

export function getMarketplaceEnablement(
  getFlag: GetFlagFn,
  marketplaceFlagName: string
): MarketplaceEnablementValue {
  const marketplaceFlagValue = getFlag(marketplaceFlagName);
  const marketplaceEnablement: MarketplaceEnablementValue = {};

  if (marketplaceFlagValue == null) {
    return marketplaceEnablement;
  }

  // Legacy LD migration shape: productCode[]
  if (Array.isArray(marketplaceFlagValue)) {
    for (const productCode of marketplaceFlagValue) {
      if (productCode == null) {
        continue;
      }
      marketplaceEnablement[String(productCode)] = [];
    }
    return marketplaceEnablement;
  }

  if (typeof marketplaceFlagValue !== 'object') {
    return marketplaceEnablement;
  }

  const catalog = marketplaceFlagValue as Record<string, unknown>;
  for (const productCode of Object.keys(catalog)) {
    const features = catalog[productCode];
    if (!Array.isArray(features)) {
      continue;
    }

    marketplaceEnablement[productCode] = [
      ...new Set(features.filter((f) => f != null).map((f) => String(f))),
    ];
  }

  return marketplaceEnablement;
}

/** Inputs for {@link resolveMarketplaceAndPlanManagementAccess}. */
export type ResolveMarketplaceAndPlanManagementAccessArgs = {
  /** Normalized merged plan map from {@link getMergedPlanEnablement} */
  mergedPlanEnablement: PlanFeatureEnablementValue;
  /** {@link RawMarketplaceCatalogLdValue} from `getFlag` for the marketplace catalog key */
  marketplaceFlagValue: RawMarketplaceCatalogLdValue;
  productCode: string;
  featureCode: string;
};

/** Result of {@link resolveMarketplaceAndPlanManagementAccess}. */
export type ResolveMarketplaceAndPlanManagementAccessResult = {
  /**
   * True when merged plan data exists, the plan map allows the product/feature pair,
   * and the marketplace catalog allows the pair. False when plan data is missing so
   * marketplace cannot surface alone (aligned with strict empty **P** everywhere).
   */
  allow: boolean;
};

/**
 * Pure resolution for a **single** `(product, feature)` pair: both merged plan map and marketplace
 * catalog must allow the pair; an empty merged plan map yields **no** allow (marketplace cannot surface
 * alone). LaunchDarkly readiness and user-context identification are left to callers — for example
 * {@link usePlanMarketplaceAccess} keeps marketplace and subscription signals off until `identify` completes.
 *
 * **Not** used for fleet Catalog / account OnStar plans **nav** visibility—that uses
 * {@link hasMarketplacePlanCatalogOverlap} and {@link usePlanManagementSurfaceEnablement} (any
 * overlapping pair, no entitlement column). This function is composed by
 * {@link usePlanMarketplaceAccess} and similar **per-feature** surfaces.
 *
 * @param args - Merged plan map, raw marketplace value, product and feature codes
 * @returns `{ allow }` for per-pair marketplace / plan-management UX
 */
export function resolveMarketplaceAndPlanManagementAccess(
  args: ResolveMarketplaceAndPlanManagementAccessArgs
): ResolveMarketplaceAndPlanManagementAccessResult {
  const {
    mergedPlanEnablement,
    marketplaceFlagValue,
    productCode,
    featureCode,
  } = args;
  const planAllowsPair = isMergedPlanProductFeatureEnabled(
    mergedPlanEnablement,
    productCode,
    featureCode
  );
  const marketplaceAllowsPair = isFeatureInMarketplaceEnablement(
    marketplaceFlagValue,
    productCode,
    featureCode
  );
  return { allow: planAllowsPair && marketplaceAllowsPair };
}
